import { MAX_COLUMNS } from "../column-presets";
import { findColumn, findGroup, findRow } from "../find";
import {
  canDeleteRow,
  canMoveRow,
  canMoveSection,
} from "../row-policy";
import type { EmailDocument, Selection } from "../types";

type BlockSelection = Selection & { kind: 'block' | 'buttonRowSlot' };

export function isBlockSelection(
  selection: Selection,
): selection is BlockSelection {
  return selection.kind === 'block' || selection.kind === 'buttonRowSlot';
}

export function blockIndexInColumn(
  document: EmailDocument,
  selection: BlockSelection,
): number {
  const column = findColumn(document, selection.rowId, selection.columnId);
  return (
    column?.blocks.findIndex(block => block.id === selection.blockId) ?? -1
  );
}

export function canMoveBlockInColumn(
  document: EmailDocument,
  selection: BlockSelection,
  direction: -1 | 1,
): boolean {
  const index = blockIndexInColumn(document, selection);
  const nextIndex = index + direction;
  const column = findColumn(document, selection.rowId, selection.columnId);
  return (
    index >= 0 &&
    column !== undefined &&
    nextIndex >= 0 &&
    nextIndex < column.blocks.length
  );
}

export function columnIndexInRow(
  document: EmailDocument,
  selection: Selection & { kind: 'column' },
): number {
  const row = findRow(document, selection.rowId);
  return (
    row?.columns.findIndex(column => column.id === selection.columnId) ?? -1
  );
}

export function canMoveColumnInRow(
  document: EmailDocument,
  selection: Selection & { kind: 'column' },
  direction: -1 | 1,
): boolean {
  const row = findRow(document, selection.rowId);
  const index = columnIndexInRow(document, selection);
  const nextIndex = index + direction;
  return (
    row !== undefined &&
    index >= 0 &&
    nextIndex >= 0 &&
    nextIndex < row.columns.length
  );
}

export function canDuplicateColumn(
  document: EmailDocument,
  selection: Selection & { kind: 'column' },
): boolean {
  const row = findRow(document, selection.rowId);
  return (row?.columns.length ?? 0) < MAX_COLUMNS;
}

export function canDeleteColumn(
  document: EmailDocument,
  selection: Selection & { kind: 'column' },
): boolean {
  const row = findRow(document, selection.rowId);
  return row?.columns.some(column => column.id === selection.columnId) ?? false;
}

export function canMoveGroup(
  document: EmailDocument,
  selection: Selection & { kind: 'group' },
  direction: -1 | 1,
): boolean {
  return canMoveSection(document, selection.groupId, direction);
}

export function canDeleteGroup(document: EmailDocument): boolean {
  return document.sections.length > 1;
}

export function canUngroupGroup(
  document: EmailDocument,
  groupId: string,
): boolean {
  return findGroup(document.sections, groupId) !== undefined;
}

export function canMoveRowSelection(
  document: EmailDocument,
  selection: Selection & { kind: 'row' },
  direction: -1 | 1,
): boolean {
  return canMoveRow(document, selection.rowId, direction);
}

export function canDeleteRowSelection(
  document: EmailDocument,
  selection: Selection & { kind: 'row' },
): boolean {
  return canDeleteRow(document, selection.rowId);
}
